/*
If any additional functionality is added to this file and the file length increases substantially,
remove this file as an exclusion in the sonar-project.properties file so it can be reevaluated
by SonarQube.
If only "duplicates" are returned by SonarQube despite any changes, the file can be re-added
to the sonar-project.properties file. 
*/

import { Button, Card, Divider, H2, List, ListItem, MenuOverlay } from '@fs/zion-ui'
import React from 'react'
import { Menu } from '@fs/zion-icon'
import { css } from '@emotion/core'

import pdf2Introduction from '../assets/2_Introduction_FS_Places.pdf'
import pdf3KeyConcepts from '../assets/3_Key_Concepts_FS_Places.pdf'
import pdf4Searching from '../assets/4_Searching_for_Places.pdf'
import pdf5Editing from '../assets/5_Editing_Entries_FS_Places.pdf'
import pdf5aParentPlace from '../assets/5a_Parent_Place.pdf'
import pdf5bPlaceType from '../assets/5b_Place_Type.pdf'
import pdf5cNativeLanguage from '../assets/5c_Native_Language.pdf'
import pdf5dCertification from '../assets/5d_Certification_Status.pdf'
import pdf5eHistoricalPeriods from '../assets/5e_Historical_Periods.pdf'
import pdf5fStartEndYear from '../assets/5f_Start_End_Year.pdf'
import pdf5gCoordinates from '../assets/5g_Coordinates.pdf'
import pdf5hNames from '../assets/5h_Names.pdf'
import pdf5iResearchLinks from '../assets/5i_Research_Links.pdf'
import pdf5jCitations from '../assets/5j_Citations.pdf'
import pdf5kHistoricalInfo from '../assets/5k_Historical_Information.pdf'
import pdf5lRelatedPlaces from '../assets/5l_Related_Places.pdf'
import pdf6CreatingEntries from '../assets/6_Creating_New_Entries_FS_Places.pdf'

export const TRAINING_NAV_ITEMS = [
  {
    id: 'intro',
    primaryText: 'Introduction to FS Places',
    secondaryText: 'Overview of the FS Places system',
    to: '#intro',
    title: 'Introduction to FS Places',
    pdfFile: pdf2Introduction,
  },
  {
    id: 'key-concepts',
    primaryText: 'Key Concepts in FS Places',
    secondaryText: 'Essential knowledge for working with places',
    to: '#key-concepts',
    title: 'Key Concepts in FS Places',
    pdfFile: pdf3KeyConcepts,
  },
  {
    id: 'searching',
    primaryText: 'Searching for Places',
    secondaryText: 'Find places effectively',
    to: '#searching',
    title: 'Searching for Places',
    pdfFile: pdf4Searching,
  },
  {
    id: 'editing',
    primaryText: 'Editing Entries in FS Places',
    heading: 'Editing Entries in FS Places',
    to: '#editing',
    title: 'Editing Entries in FS Places',
    pdfFile: pdf5Editing,
    subItems: [
      {
        id: 'parent-place',
        primaryText: 'Parent Place',
        secondaryText: 'Working with hierarchical relationships',
        to: '#parent-place',
        title: 'Parent Place',
        pdfFile: pdf5aParentPlace,
      },
      {
        id: 'place-type',
        primaryText: 'Place Type',
        secondaryText: 'Understanding place categories',
        to: '#place-type',
        title: 'Place Type',
        pdfFile: pdf5bPlaceType,
      },
      {
        id: 'native-language',
        primaryText: 'Native Language',
        secondaryText: 'Setting appropriate languages',
        to: '#native-language',
        title: 'Native Language',
        pdfFile: pdf5cNativeLanguage,
      },
      {
        id: 'certification',
        primaryText: 'Certification Status',
        secondaryText: 'Quality indicators for places',
        to: '#certification',
        title: 'Certification Status',
        pdfFile: pdf5dCertification,
      },
      {
        id: 'historical-periods',
        primaryText: 'Historical Periods',
        secondaryText: 'Managing time-based information',
        to: '#historical-periods',
        title: 'Historical Periods',
        pdfFile: pdf5eHistoricalPeriods,
      },
      {
        id: 'start-end-year',
        primaryText: 'Start Year & End Year',
        secondaryText: 'Setting place existence timeframes',
        to: '#start-end-year',
        title: 'Start Year & End Year',
        pdfFile: pdf5fStartEndYear,
      },
      {
        id: 'coordinates',
        primaryText: 'Coordinates',
        secondaryText: 'Setting geographical location',
        to: '#coordinates',
        title: 'Coordinates',
        pdfFile: pdf5gCoordinates,
      },
      {
        id: 'names',
        primaryText: 'Names',
        secondaryText: 'Managing multiple place names',
        to: '#names',
        title: 'Names',
        pdfFile: pdf5hNames,
      },
      {
        id: 'research-links',
        primaryText: 'Research Links',
        secondaryText: 'Adding helpful resources',
        to: '#research-links',
        title: 'Research Links',
        pdfFile: pdf5iResearchLinks,
      },
      {
        id: 'citations',
        primaryText: 'Citations',
        secondaryText: 'Documenting your sources',
        to: '#citations',
        title: 'Citations',
        pdfFile: pdf5jCitations,
      },
      {
        id: 'historical-info',
        primaryText: 'Historical Information',
        secondaryText: 'Adding contextual details',
        to: '#historical-info',
        title: 'Historical Information',
        pdfFile: pdf5kHistoricalInfo,
      },
      {
        id: 'related-places',
        primaryText: 'Related Places',
        secondaryText: 'Connecting associated locations',
        to: '#related-places',
        title: 'Related Places',
        pdfFile: pdf5lRelatedPlaces,
      },
    ],
  },
  {
    id: 'creating-entries',
    primaryText: 'Creating New Entries in FS Places',
    secondaryText: 'Adding new places to the database',
    to: '#creating-entries',
    title: 'Creating New Entries in FS Places',
    pdfFile: pdf6CreatingEntries,
  },
]

// ----- Navigation rendering components -----

function TrainingNavListItems() {
  return (
    <>
      {TRAINING_NAV_ITEMS.map((item, index) => (
        <React.Fragment key={item.id}>
          {/* Insert divider before items that have subItems and are not the first item */}
          {item.subItems && index !== 0 && <Divider marginY="sm" />}

          {/* Top-level clickable item */}
          <ListItem
            dense
            to={item.to || `#${item.id}`}
            primaryText={item.primaryText || item.title}
            secondaryText={item.secondaryText}
          />

          {/* Nested items if any */}
          {item.subItems?.map((subItem) => (
            <ListItem
              key={subItem.id}
              dense
              to={subItem.to || `#${subItem.id}`}
              primaryText={subItem.primaryText}
              secondaryText={subItem.secondaryText}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  )
}

// ----- Side nav panel -----

export default function PlaceEditorTrainingNav() {
  return (
    <div
      id="place-editor-training-nav"
      css={css`
        display: flex;
        flex-direction: column;
        height: calc(100vh - 160px);
        overflow-y: auto;
      `}
    >
      <div
        css={css`
          padding-bottom: 16px;
        `}
      >
        <Card margins="md">
          <H2 size="H5">Training Topics</H2>
          <Divider marginY="xs" />
          <List>
            <TrainingNavListItems />
          </List>
        </Card>
      </div>
    </div>
  )
}

// ----- Mobile menu dropdown -----

export function PlaceEditorNavMenu() {
  return (
    <MenuOverlay menuButton={<Button Icon={Menu}>Open Training Menu</Button>} inline={false}>
      <TrainingNavListItems />
    </MenuOverlay>
  )
}
